import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWindows, AppId, APPS } from '../context/WindowsContext';
interface WindowProps {
  id: AppId;
  children: React.ReactNode;
  className?: string;
}
export const Window: React.FC<WindowProps> = ({
  id,
  children,
  className = ''
}) => {
  const {
    openApps,
    minimizedApps,
    focusedApp,
    zIndices,
    closeApp,
    minimizeApp,
    focusApp
  } = useWindows();
  const app = APPS[id];
  const windowRef = useRef<HTMLDivElement>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: app.defaultWidth,
    height: app.defaultHeight
  });
  const isOpen = openApps.includes(id);
  const isMinimized = minimizedApps.includes(id);
  const isFocused = focusedApp === id;
  const zIndex = zIndices[id] || 10;
  // Handle screen resize for maximized state
  useEffect(() => {
    const handleResize = () => {
      if (isMaximized) {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight - 28
        }); // 28px for menu bar
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMaximized]);
  const toggleMaximize = () => {
    if (isMaximized) {
      setWindowSize({
        width: app.defaultWidth,
        height: app.defaultHeight
      });
      setIsMaximized(false);
    } else {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight - 28
      });
      setIsMaximized(true);
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-381" data-mp="e1dkocjifosw">
      {!isMinimized && <motion.div ref={windowRef} initial={{
      opacity: 0,
      scale: 0.8,
      y: 50
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0,
      width: windowSize.width,
      height: windowSize.height,
      top: isMaximized ? 28 : undefined,
      left: isMaximized ? 0 : undefined
    }} exit={{
      opacity: 0,
      scale: 0.8,
      y: 50
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} style={{
      zIndex
    }} className={`absolute rounded-xl overflow-hidden bg-mac-dark border border-mac-border shadow-mac-window flex flex-col ${className}`} drag={!isMaximized} dragMomentum={false} dragHandle=".window-drag-handle" onPointerDown={() => focusApp(id)}
    // Center initially if not maximized
    initialCapture={{
      x: 'calc(50vw - 50%)',
      y: 'calc(50vh - 50%)'
    }} data-id="element-382" data-mp="ix19gqu2yfxv">
          {/* Title Bar */}
          <div className={`window-drag-handle h-12 flex items-center px-4 bg-mac-dark/80 backdrop-blur-md border-b border-white/5 select-none ${!isMaximized ? 'cursor-grab active:cursor-grabbing' : ''}`} onDoubleClick={toggleMaximize} data-id="element-383" data-mp="3h9fqmwapvp08">
            {/* Traffic Lights */}
            <div className="flex items-center gap-2 group w-20" data-id="element-384" data-mp="2ddsrps1cow8">
              <button onClick={e => {
            e.stopPropagation();
            closeApp(id);
          }} className="w-3 h-3 rounded-full bg-[#ff5f56] border border-[#e0443e] flex items-center justify-center" data-id="element-385" data-mp="2g9p59o8j5ik">
                <span className="opacity-0 group-hover:opacity-100 text-[#990000] text-[8px] leading-none font-bold" data-id="element-386" data-mp="3atz2j8shrm0e">
                  ✕
                </span>
              </button>
              <button onClick={e => {
            e.stopPropagation();
            minimizeApp(id);
          }} className="w-3 h-3 rounded-full bg-[#ffbd2e] border border-[#dea123] flex items-center justify-center" data-id="element-387" data-mp="3jvyli6n2h05z">
                <span className="opacity-0 group-hover:opacity-100 text-[#995700] text-[8px] leading-none font-bold" data-id="element-388" data-mp="xo24hbsbj4wj">
                  −
                </span>
              </button>
              <button onClick={e => {
            e.stopPropagation();
            toggleMaximize();
          }} className="w-3 h-3 rounded-full bg-[#27c93f] border border-[#1aab29] flex items-center justify-center" data-id="element-389" data-mp="17lq4oh8p14m3">
                <span className="opacity-0 group-hover:opacity-100 text-[#006500] text-[8px] leading-none font-bold" data-id="element-390" data-mp="1bn1tikjqtlt8">
                  ＋
                </span>
              </button>
            </div>

            {/* Title */}
            <div className="flex-1 text-center text-sm font-medium text-white/80 pr-20" data-id="element-391" data-mp="1ldmqk3jrqia1">
              {app.title}
            </div>
          </div>

          {/* Window Content */}
          <div className="flex-1 overflow-hidden relative bg-mac-dark" data-id="element-392" data-mp="1vbawlpmtrc7i">
            {/* Overlay to block interaction when not focused */}
            {!isFocused && <div className="absolute inset-0 z-50 bg-transparent" data-id="element-393" data-mp="2dx8puwc49n9g" />}
            {children}
          </div>
        </motion.div>}
    </AnimatePresence>;
};